import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { DatePicker, message, Modal, Select, Table, TableProps, Tooltip } from "antd";
import dayjs from "dayjs";
import { FaEye, FaSearch, FaUndo } from "react-icons/fa";
import Spinner from "../../components/shared/Spinner/Spinner";
import MainButton from "../../components/shared/MainButton/MainButton";
import { PaymentItemType, PaymentsFilterParams } from "../../interfaces/admin.interface";
import { PaymentsService } from "../../services/payments.service";
import styles from "./Payments.module.scss";

interface InitialPaymentFilterState {
  order_code: string;
  user_id: string;
  type: string;
  site_pay: string;
  transaction_id: string;
  epoint_transaction_id: string;
  start_date: string;
  end_date: string;
}

const initialFilters: InitialPaymentFilterState = {
  order_code: "",
  user_id: "",
  type: "",
  site_pay: "",
  transaction_id: "",
  epoint_transaction_id: "",
  start_date: "",
  end_date: "",
};

const Payments = () => {
  const [paymentsList, setPaymentsList] = useState<PaymentItemType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterValues, setFilterValues] = useState<InitialPaymentFilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<InitialPaymentFilterState>(initialFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalSum, setTotalSum] = useState<string>("0.00");
  const [selectedPayment, setSelectedPayment] = useState<PaymentItemType | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  const handleFetchPayments = async (page: number, filtersToApply: InitialPaymentFilterState) => {
    setIsLoading(true);

    const params: PaymentsFilterParams = {
      page,
      ...filtersToApply,
    };

    const response = await PaymentsService.getPayments(params);

    if (response?.status !== 200 || !response?.data?.payments) {
      setIsLoading(false);
      message.error("Ödənişləri əldə etmək mümkün olmadı");
      return;
    }

    const paymentsData = response.data.payments;
    const items = Array.isArray(paymentsData?.data) ? paymentsData.data : [];

    setPaymentsList(items);
    setTotalCount(paymentsData?.total || items.length);
    setTotalSum(response.data.sum || "0.00");
    setCurrentPage(paymentsData?.current_page || page);
    if (paymentsData?.per_page) {
      setPageSize(paymentsData.per_page);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleFetchPayments(1, appliedFilters);
  }, []);

  const handleInputChange = (field: keyof InitialPaymentFilterState, value: string) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleKeyDownFilter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    handleSearch();
  };

  const handleStartDateChange = (_: any, dateString: string | string[]) => {
    const stringVal = Array.isArray(dateString) ? dateString[0] : dateString;
    setFilterValues((prev) => ({ ...prev, start_date: stringVal }));
  };

  const handleEndDateChange = (_: any, dateString: string | string[]) => {
    const stringVal = Array.isArray(dateString) ? dateString[0] : dateString;
    setFilterValues((prev) => ({ ...prev, end_date: stringVal }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedFilters(filterValues);
    handleFetchPayments(1, filterValues);
  };

  const handleResetFilters = () => {
    setFilterValues(initialFilters);
    setAppliedFilters(initialFilters);
    setCurrentPage(1);
    handleFetchPayments(1, initialFilters);
  };

  const handleViewDetails = (payment: PaymentItemType) => {
    setSelectedPayment(payment);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPayment(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleFetchPayments(page, appliedFilters);
  };

  const renderTypeBadge = (type: string) => {
    if (type === "order") {
      return <span className={styles.badge_type_order}>Sifariş</span>;
    }
    if (type === "delivery") {
      return <span className={styles.badge_type_delivery}>Çatdırılma</span>;
    }
    return <span className={styles.badge_type_other}>{type || "-"}</span>;
  };

  const renderSitePayBadge = (sitePay: number) => {
    if (sitePay === 1) {
      return <span className={styles.badge_site_web}>Saytdan</span>;
    }
    if (sitePay === 0 || sitePay === 2) {
      return <span className={styles.badge_site_mobile}>Mobil</span>;
    }
    return <span className={styles.badge_site_other}>Digər ({sitePay})</span>;
  };

  const tableColumns: TableProps<PaymentItemType>["columns"] = [
    {
      title: "№",
      key: "row_number",
      width: 60,
      align: "center",
      render: (_, __, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      sorter: (a, b) => a.id - b.id,
      render: (id: number) => <span style={{ fontWeight: 600 }}>{id}</span>,
    },
    {
      title: "Sifariş Kodu",
      dataIndex: "order_code",
      key: "order_code",
      render: (code: string) => (
        <span className={styles.order_code_badge}>{code || "-"}</span>
      ),
    },
    {
      title: "Müştəri",
      key: "customer",
      render: (_: any, record: PaymentItemType) => (
        <div className={styles.customer_cell}>
          <span className={styles.customer_name}>
            {record.name} {record.surname}
          </span>
          <span className={styles.customer_contact}>
            {record.email || record.number
              ? `${record.email || ""} ${record.number ? `(+994 ${record.number})` : ""}`.trim()
              : "-"}
          </span>
        </div>
      ),
    },
    {
      title: "İstifadəçi ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 100,
      render: (userId: number) => <span>{userId || "-"}</span>,
    },
    {
      title: "Məbləğ",
      dataIndex: "price",
      key: "price",
      render: (price: string) => (
        <span className={styles.price_tag}>{price} ₼</span>
      ),
    },
    {
      title: "Növ",
      dataIndex: "type",
      key: "type",
      render: (type: string) => renderTypeBadge(type),
    },
    {
      title: "Ödəniş Mənbəyi",
      dataIndex: "site_pay",
      key: "site_pay",
      render: (sitePay: number) => renderSitePayBadge(sitePay),
    },
    {
      title: "Tranzaksiya ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      render: (txId: string) => (
        <Tooltip title={txId}>
          <span className={styles.id_text}>{txId || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Epoint ID",
      dataIndex: "epoint_transaction_id",
      key: "epoint_transaction_id",
      render: (epointId: string) => (
        <Tooltip title={epointId}>
          <span className={styles.id_text}>{epointId || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Tarix",
      dataIndex: "created_at",
      key: "created_at",
      render: (createdAt: string) => (
        <span className={styles.date_text}>
          {createdAt ? dayjs(createdAt).format("DD.MM.YYYY HH:mm") : "-"}
        </span>
      ),
    },
    {
      title: "Əməliyyatlar",
      key: "actions",
      width: 100,
      align: "center",
      render: (_: any, record: PaymentItemType) => (
        <div className={styles.action_buttons}>
          <button
            type="button"
            className={`${styles.action_btn} ${styles.view_btn}`}
            onClick={() => handleViewDetails(record)}
            aria-label={`Ödəniş detallarına bax ${record.id}`}
            tabIndex={0}
          >
            <FaEye size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Ödənişlər</title>
      </Helmet>

      <div className={styles.payments_container}>
        <div className={styles.page_header}>
          <div className={styles.header_left}>
            <h1 className={styles.page_title}>Ödənişlər</h1>
            <span className={styles.total_badge}>
              Toplam ödəniş: <strong>{totalCount}</strong>
            </span>
          </div>

          <div className={styles.header_right}>
            <div className={styles.sum_badge} aria-label="Ümumi dövriyyə">
              Ümumi dövriyyə: <strong>{totalSum} ₼</strong>
            </div>
          </div>
        </div>

        <section aria-label="Ödəniş filtrləri" className={styles.filter_card}>
          <div className={styles.filter_grid}>
            <div className={styles.filter_item}>
              <label htmlFor="filter_order_code">Sifariş kodu</label>
              <input
                id="filter_order_code"
                type="text"
                placeholder="Sifariş kodu daxil edin"
                value={filterValues.order_code}
                onChange={(e) => handleInputChange("order_code", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                className={styles.filter_input}
                aria-label="Sifariş kodu üzrə axtarış"
                tabIndex={0}
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter_user_id">İstifadəçi ID</label>
              <input
                id="filter_user_id"
                type="text"
                placeholder="İstifadəçi ID daxil edin"
                value={filterValues.user_id}
                onChange={(e) => handleInputChange("user_id", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                className={styles.filter_input}
                aria-label="İstifadəçi ID üzrə axtarış"
                tabIndex={0}
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter_type">Ödəniş növü</label>
              <Select
                id="filter_type"
                placeholder="Hamısı"
                value={filterValues.type || undefined}
                onChange={(val) => handleInputChange("type", val || "")}
                allowClear
                className={styles.filter_select}
                options={[
                  { value: "order", label: "Sifariş (Order)" },
                  { value: "delivery", label: "Çatdırılma (Delivery)" },
                ]}
                aria-label="Ödəniş növü filtri"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter_site_pay">Ödəniş mənbəyi</label>
              <Select
                id="filter_site_pay"
                placeholder="Hamısı"
                value={filterValues.site_pay || undefined}
                onChange={(val) => handleInputChange("site_pay", val || "")}
                allowClear
                className={styles.filter_select}
                options={[
                  { value: "1", label: "Saytdan" },
                  { value: "2", label: "Mobil" },
                ]}
                aria-label="Ödəniş mənbəyi filtri"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter_tx_id">Tranzaksiya ID</label>
              <input
                id="filter_tx_id"
                type="text"
                placeholder="Daxili tranzaksiya ID"
                value={filterValues.transaction_id}
                onChange={(e) => handleInputChange("transaction_id", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                className={styles.filter_input}
                aria-label="Tranzaksiya ID üzrə axtarış"
                tabIndex={0}
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter_epoint_id">Epoint Tranzaksiya ID</label>
              <input
                id="filter_epoint_id"
                type="text"
                placeholder="Epoint tranzaksiya ID"
                value={filterValues.epoint_transaction_id}
                onChange={(e) => handleInputChange("epoint_transaction_id", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                className={styles.filter_input}
                aria-label="Epoint ID üzrə axtarış"
                tabIndex={0}
              />
            </div>

            <div className={styles.filter_item}>
              <label>Başlanğıc tarixi</label>
              <DatePicker
                className={styles.filter_date}
                placeholder="Başlanğıc tarixi"
                format="YYYY-MM-DD"
                value={filterValues.start_date ? dayjs(filterValues.start_date) : null}
                onChange={handleStartDateChange}
                aria-label="Başlanğıc tarixi filtri"
              />
            </div>

            <div className={styles.filter_item}>
              <label>Bitmə tarixi</label>
              <DatePicker
                className={styles.filter_date}
                placeholder="Bitmə tarixi"
                format="YYYY-MM-DD"
                value={filterValues.end_date ? dayjs(filterValues.end_date) : null}
                onChange={handleEndDateChange}
                aria-label="Bitmə tarixi filtri"
              />
            </div>
          </div>

          <div className={styles.filter_actions}>
            <button
              type="button"
              className={styles.reset_btn}
              onClick={handleResetFilters}
              aria-label="Filtrləri sıfırla"
              tabIndex={0}
            >
              <FaUndo size={13} />
              Sıfırla
            </button>
            <MainButton onClick={handleSearch}>
              <div className={styles.search_btn_content}>
                <FaSearch size={13} />
                <span>Axtar</span>
              </div>
            </MainButton>
          </div>
        </section>

        <section aria-label="Ödəniş cədvəli" className={styles.table_card}>
          {isLoading ? (
            <div style={{ padding: "64px 0", display: "flex", justifyContent: "center" }}>
              <Spinner />
            </div>
          ) : (
            <div className={styles.table_wrapper}>
              <Table<PaymentItemType>
                rowKey={(record) => String(record.id)}
                columns={tableColumns}
                dataSource={paymentsList}
                scroll={{ x: 1100 }}
                locale={{
                  emptyText: "Məlumat tapılmadı",
                  triggerDesc: "Azalan sıra ilə düz",
                  triggerAsc: "Artan sıra ilə düz",
                  cancelSort: "Sıralamanı ləğv et",
                }}
                pagination={{
                  current: currentPage,
                  pageSize,
                  total: totalCount,
                  showSizeChanger: false,
                  onChange: handlePageChange,
                  showTotal: (total, range) => (
                    <span className={styles.total_text}>
                      Göstərilir: {range[0]}-{range[1]} / Cəmi: {total}
                    </span>
                  ),
                }}
              />
            </div>
          )}
        </section>

        <Modal
          title={<div className={styles.modal_title}>Ödəniş Detalları #{selectedPayment?.id || ""}</div>}
          open={isDetailModalOpen}
          onCancel={handleCloseModal}
          footer={null}
          width={650}
        >
          {selectedPayment && (
            <div>
              <div className={styles.info_list}>
                <div className={styles.info_row}>
                  <div className={styles.info_label}>Məbləğ:</div>
                  <div className={styles.info_value} style={{ color: "#059669", fontWeight: 700, fontSize: 16 }}>
                    {selectedPayment.price} ₼
                  </div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Ödəniş Növü:</div>
                  <div className={styles.info_value}>{renderTypeBadge(selectedPayment.type)}</div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Ödəniş Mənbəyi:</div>
                  <div className={styles.info_value}>{renderSitePayBadge(selectedPayment.site_pay)}</div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Sifariş Kodu:</div>
                  <div className={styles.info_value}>
                    <span className={styles.code_value}>{selectedPayment.order_code || "-"}</span>
                  </div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Sifariş ID:</div>
                  <div className={styles.info_value}>{selectedPayment.order_id || "-"}</div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>İstifadəçi ID:</div>
                  <div className={styles.info_value}>{selectedPayment.user_id || "-"}</div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Müştəri:</div>
                  <div className={styles.info_value} style={{ fontWeight: 600 }}>
                    {selectedPayment.name} {selectedPayment.surname}
                  </div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>E-poçt:</div>
                  <div className={styles.info_value}>{selectedPayment.email || "-"}</div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Əlaqə nömrəsi:</div>
                  <div className={styles.info_value}>{selectedPayment.number || "-"}</div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Daxili Tranzaksiya ID:</div>
                  <div className={styles.info_value}>
                    <span className={styles.code_value}>{selectedPayment.transaction_id || "-"}</span>
                  </div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Epoint Tranzaksiya ID:</div>
                  <div className={styles.info_value}>
                    <span className={styles.code_value}>{selectedPayment.epoint_transaction_id || "-"}</span>
                  </div>
                </div>

                <div className={styles.info_row}>
                  <div className={styles.info_label}>Tarix:</div>
                  <div className={styles.info_value}>
                    {selectedPayment.created_at
                      ? dayjs(selectedPayment.created_at).format("DD.MM.YYYY HH:mm:ss")
                      : "-"}
                  </div>
                </div>
              </div>

              <div className={styles.modal_footer}>
                <button
                  type="button"
                  className={styles.modal_close_btn}
                  onClick={handleCloseModal}
                  tabIndex={0}
                  aria-label="Bağla"
                >
                  Bağla
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Payments;
